// ============================================================================
// ComitesPage — liste des comites (E3a, F-400..F-407) — /comites
// ============================================================================
// Portage du PoC (d)/comites/page.tsx au standard maison :
//   - garde d'entree SERVEUR (M-402) : GET /api/committees 403 pour un
//     non-management -> ecran de refus i18n (F-400) ;
//   - filtre Actifs/Tous en Segmented deep-linkable ?status=all (F-401) ;
//   - alerte ?error= mappee i18n (F-402, GA-21) ;
//   - « Nouveau comite » ADMIN -> wizard 3 etapes (F-403/F-404, btnAction) ;
//   - cartes drill-in -> fiche (F-406) : nom, description, ProgressBar jalons
//     done/total, nb membres, derniere MAJ, date cible, badge statut ;
//   - empty state selon le filtre (F-405) ; skeleton (F-407).

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  PageHeader, EmptyState, StatusBadge, GsProgressBar, SkeletonCard, QueryError,
  GsSegmented, GS_COMPONENTS,
} from '@gs/ui';
import { Plus, ShieldAlert, UsersRound } from 'lucide-react';
import { ApiForbiddenError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import { useCommittees, type CommitteeSummary, type CommitteeStatus } from '../../hooks/useCommittees';
import { NewCommitteeDialog } from '../../components/comites/NewCommitteeDialog';
import { formatDate, formatDateOnly } from './dates';

const STATUS_BADGE: Record<CommitteeStatus, 'success' | 'warning' | 'neutral'> = {
  active: 'success',
  paused: 'warning',
  completed: 'neutral',
};

export function CommitteeStatusBadge({ status }: { status: CommitteeStatus }) {
  const { t } = useTranslation();
  return <StatusBadge variant={STATUS_BADGE[status]}>{t(`gest:comites.status.${status}`)}</StatusBadge>;
}

/** Ecran de refus management (M-402) — l'autorite est le 403 serveur. */
export function CommitteesForbidden() {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<ShieldAlert size={48} aria-hidden="true" />}
      title={t('gest:comites.forbidden.title')}
      description={t('gest:comites.forbidden.description')}
    />
  );
}

function CommitteeCard({ committee }: { committee: CommitteeSummary }) {
  const { t, i18n } = useTranslation();
  return (
    <Link
      to={`/comites/${committee.id}`}
      className={`${GS_COMPONENTS.card} flex touch-manipulation flex-col gap-3 p-5 transition-colors hover:border-slate-300 dark:hover:border-slate-500`}
    >
      <header className="flex items-start justify-between gap-3">
        <h3 className="min-w-0 truncate text-field font-semibold text-slate-900 dark:text-slate-100">
          {committee.name}
        </h3>
        <CommitteeStatusBadge status={committee.status} />
      </header>
      {committee.description ? (
        <p className="line-clamp-2 text-field text-slate-500 dark:text-slate-400">{committee.description}</p>
      ) : null}
      {committee.milestonesTotal > 0 ? (
        <GsProgressBar value={committee.milestonesDone} max={committee.milestonesTotal} label />
      ) : null}
      <div className="mt-auto flex flex-wrap items-baseline gap-3 text-meta text-slate-500 dark:text-slate-400">
        <span>{t('gest:comites.card.members', { count: committee.memberCount })}</span>
        <span>
          {committee.lastUpdateAt
            ? t('gest:comites.card.lastUpdate', { date: formatDate(i18n.language, committee.lastUpdateAt) })
            : t('gest:comites.card.noUpdate')}
        </span>
        {committee.targetDate ? (
          <span>{t('gest:comites.card.target', { date: formatDateOnly(i18n.language, committee.targetDate) })}</span>
        ) : null}
      </div>
    </Link>
  );
}

export default function ComitesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dialogOpen, setDialogOpen] = useState(false);

  const showAll = searchParams.get('status') === 'all'; // F-401 deep-link
  const errorCode = searchParams.get('error'); // F-402 deep-link

  const listQ = useCommittees(showAll, !!user);

  function setError(code: string) {
    const next = new URLSearchParams(searchParams);
    next.set('error', code);
    setSearchParams(next);
  }

  function setStatusFilter(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.set('status', 'all');
    else next.delete('status');
    next.delete('error');
    setSearchParams(next);
  }

  const forbidden = listQ.isError && listQ.error instanceof ApiForbiddenError;

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('gest:comites.title')}
        onRefresh={() => void listQ.refetch()}
        refreshLoading={listQ.isFetching}
        actions={
          // F-401 : Segmented Actifs / Tous — deep-link via ?status= (GsSegmented).
          <GsSegmented
            ariaLabel={t('gest:comites.filter.label')}
            value={showAll ? 'all' : 'active'}
            onChange={setStatusFilter}
            options={[
              { value: 'active', label: t('gest:comites.filter.active') },
              { value: 'all', label: t('gest:comites.filter.all') },
            ]}
          />
        }
      />

      {errorCode ? (
        // F-402 : bannière d'état ?error= — liseré gauche (SM-5 / variante B), pas fond teinté.
        <div
          role="alert"
          className="rounded-xl border border-slate-200 border-l-3 border-l-red-500 bg-white px-4 py-3 text-field text-red-700 dark:border-slate-700 dark:border-l-red-500 dark:bg-slate-800 dark:text-red-300"
        >
          {t(`gest:comites.errors.${['invalid', 'forbidden', 'notfound', 'server'].includes(errorCode) ? errorCode : 'server'}`)}
        </div>
      ) : null}

      {forbidden ? (
        <CommitteesForbidden />
      ) : listQ.isLoading ? (
        // F-407 : skeleton liste.
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : listQ.isError ? (
        <QueryError message={t('gest:comites.loadError')} onRetry={() => void listQ.refetch()} />
      ) : listQ.data ? (
        <>
          {listQ.data.viewer.canCreate ? (
            // F-403 : ADMIN seulement — CTA primaire tonal (btnAction, jamais rouge).
            <div>
              <button
                type="button"
                onClick={() => setDialogOpen(true)}
                className={`${GS_COMPONENTS.btnAction} inline-flex items-center gap-1.5`}
              >
                <Plus size={16} aria-hidden="true" />
                {t('gest:comites.new')}
              </button>
            </div>
          ) : null}

          {listQ.data.committees.length === 0 ? (
            // F-405 : empty state selon le filtre.
            <EmptyState
              icon={<UsersRound size={48} aria-hidden="true" />}
              title={showAll ? t('gest:comites.emptyAll') : t('gest:comites.emptyActive')}
            />
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {listQ.data.committees.map((c) => (
                <CommitteeCard key={c.id} committee={c} />
              ))}
            </div>
          )}
        </>
      ) : null}

      <NewCommitteeDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onError={setError} />
    </div>
  );
}
